import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';

import styles from './DoseField.module.css';

export interface DoseFieldProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'style' | 'inputMode'
> {
  label: string;
  /** The dose unit, inside the right edge: "tablet", "mL", "mg". */
  unit: string;
  description?: string;
  /** Why the dose can't be used, e.g. "Enter the dose as a number above zero." */
  errorMessage?: string;
}

/**
 * A dose as a prescriber types it (consultation plan §7): a decimal number with its unit
 * inside the right edge and `inputmode="decimal"`. "." and "," are both accepted; the text
 * is parsed by `parseDose` in @wodi/domain, never as a floating point number.
 */
export function DoseField({
  label,
  unit,
  description,
  errorMessage,
  isInvalid,
  ...rest
}: DoseFieldProps) {
  return (
    <AriaTextField
      {...rest}
      inputMode="decimal"
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.field}
    >
      <Label className={styles.label}>
        <span aria-hidden="true">{label}</span>
        <span className={styles.visuallyHidden}>{`${label} (${unit})`}</span>
      </Label>
      <div className={styles.control}>
        <Input className={styles.input} autoComplete="off" />
        <span className={styles.unit} aria-hidden="true">
          {unit}
        </span>
      </div>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </AriaTextField>
  );
}
